import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import styles from "./MissingProductOptions.module.scss";
import { DIRECT_ACCESS_URL, IMAGE_BASE_URL, ORDER_OPTION_UPDATE_TOKEN_SECRET } from "../../constants/constants";

type TokenPayload = {
  order_id: number | string;
  email: string;
  exp?: number;
  iat?: number;
};

type ProductOptionDefinition = {
  key: string;
  optionType: number;
  inputType: number;
  label: string;
  values: string[];
};

const base64UrlToBase64 = (value: string) =>
  value.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(value.length / 4) * 4, "=");

const decodeBase64Url = (value: string) => {
  try {
    return decodeURIComponent(
      atob(base64UrlToBase64(value))
        .split("")
        .map((char) => `%${`00${char.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join("")
    );
  } catch (error) {
    return "";
  }
};

const toBase64Url = (bytes: Uint8Array) =>
  btoa(String.fromCharCode(...Array.from(bytes)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/g, "");

const verifyHs512Token = async (token: string, secret: string): Promise<TokenPayload | null> => {
  const [encodedHeader, encodedPayload, encodedSignature] = token.split(".");
  if (!encodedHeader || !encodedPayload || !encodedSignature) return null;

  const headerString = decodeBase64Url(encodedHeader);
  const payloadString = decodeBase64Url(encodedPayload);
  if (!headerString || !payloadString) return null;

  let parsedHeader: { alg?: string };
  let parsedPayload: TokenPayload;
  try {
    parsedHeader = JSON.parse(headerString);
    parsedPayload = JSON.parse(payloadString);
  } catch (error) {
    return null;
  }

  if (parsedHeader.alg !== "HS512") return null;

  const key = await window.crypto.subtle.importKey(
    "raw",
    new TextEncoder().encode(secret),
    { name: "HMAC", hash: "SHA-512" },
    false,
    ["sign"]
  );
  const signed = await window.crypto.subtle.sign(
    "HMAC",
    key,
    new TextEncoder().encode(`${encodedHeader}.${encodedPayload}`)
  );
  const computedSignature = toBase64Url(new Uint8Array(signed));
  if (computedSignature !== encodedSignature) return null;

  if (parsedPayload.exp && Math.floor(Date.now() / 1000) > Number(parsedPayload.exp)) {
    return null;
  }

  if (!parsedPayload.order_id || !parsedPayload.email) return null;
  return parsedPayload;
};

const getOptionValues = (option: any) => {
  const productOptionItems = option?.store_product_option_items || [];
  if (productOptionItems.length > 0) {
    return productOptionItems
      .map((item: any) => item?.option_name)
      .filter((name: string) => Boolean(name));
  }

  const storeOptionItems = option?.store_option_items || [];
  return storeOptionItems
    .map((item: any) => item?.option_name)
    .filter((name: string) => Boolean(name));
};

const buildSelectableOptions = (productDetail: any): ProductOptionDefinition[] => {
  const baseOptions = productDetail?.store_options || [];
  const attachedProductOptions = productDetail?.store_product_options || [];

  return baseOptions
    .map((baseOption: any) => {
      const productOption = attachedProductOptions.find(
        (item: any) => item?.store_option_id === baseOption?.id
      );
      const values = getOptionValues(productOption || baseOption);
      const optionStatus =
        productOption?.status ?? baseOption?.status ?? 1;
      const inputType = Number(
        productOption?.input_type ?? baseOption?.input_type ?? 1
      );
      const isEnabled =
        Number(optionStatus) === 1 &&
        !productOption?.deleted_at &&
        !baseOption?.deleted_at;
      if (!isEnabled) return null;
      if (inputType === 1 && values.length === 0) return null;

      return {
        key: String(baseOption?.id),
        optionType: Number(baseOption?.option_type || 0),
        inputType,
        label: productOption?.display_name || baseOption?.display_name || baseOption?.internal_name || "Option",
        values,
      };
    })
    .filter((option: ProductOptionDefinition | null): option is ProductOptionDefinition => Boolean(option));
};

const MissingProductOptions = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [orderData, setOrderData] = useState<any>(null);
  const [optionsByProductId, setOptionsByProductId] = useState<Record<string, ProductOptionDefinition[]>>({});
  const [selectedOptions, setSelectedOptions] = useState<Record<string, Record<string, string>>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmittedSuccessfully, setIsSubmittedSuccessfully] = useState(false);
  const [isOrderUpToDate, setIsOrderUpToDate] = useState(false);

  const tokenSecret = ORDER_OPTION_UPDATE_TOKEN_SECRET;

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      setErrorMessage("");
      try {
        const token = searchParams.get("token");
        if (!token) {
          navigate("/page-not-found", { replace: true });
          return;
        }

        const orderRes = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/get-order-status-by-token/${DIRECT_ACCESS_URL}`,
          {
            headers: {
              "x-auth": token,
            },
          }
        );
        const fetchedOrder = orderRes?.data?.data;
        if (!fetchedOrder) {
          navigate("/page-not-found", { replace: true });
          return;
        }

        const orderProducts = fetchedOrder?.order_products || [];
        const storeProductIds = Array.from(
          new Set(
            orderProducts
              .map((item: any) => item?.store_product_id)
              .filter((id: number | null) => Boolean(id))
          )
        );

        const productResponses = await Promise.all(
          storeProductIds.map(async (storeProductId: any) => {
            const response = await axios.get(
              `${process.env.REACT_APP_BASE_URL}/get-products-info/public/${storeProductId}`
            );
            return {
              storeProductId: String(storeProductId),
              productData: response?.data?.data,
            };
          })
        );

        const optionMap: Record<string, ProductOptionDefinition[]> = {};
        productResponses.forEach((item) => {
          optionMap[item.storeProductId] = buildSelectableOptions(item.productData);
        });

        const initialSelection: Record<string, Record<string, string>> = {};
        orderProducts.forEach((orderProduct: any) => {
          const optionDefinitions = optionMap[String(orderProduct?.store_product_id)] || [];
          const currentOrderOptions = orderProduct?.order_product_options || [];
          const optionSelection: Record<string, string> = {};

          optionDefinitions.forEach((definition) => {
            const matchedOption = currentOrderOptions.find(
              (option: any) =>
                Number(option?.product_option_type) === definition.optionType &&
                definition.values.includes(option?.product_option_value)
            );
            optionSelection[definition.key] = matchedOption?.product_option_value || "";
          });
          initialSelection[String(orderProduct.id)] = optionSelection;
        });

        const allProductsHaveOptions =
          orderProducts.length > 0 &&
          orderProducts.every(
            (product: any) =>
              Array.isArray(product?.order_product_options) &&
              product.order_product_options.length > 0
          );
        if (allProductsHaveOptions) {
          navigate("/page-not-found", { replace: true });
          return;
        }

        setOrderData(fetchedOrder);
        setOptionsByProductId(optionMap);
        setSelectedOptions(initialSelection);
        setIsOrderUpToDate(false);
      } catch (error: any) {
        if (error?.response?.status === 401 || error?.response?.status === 404) {
          navigate("/page-not-found", { replace: true });
          return;
        }
        setErrorMessage("Unable to load order details. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [searchParams, tokenSecret, navigate]);

  const handleOptionChange = (orderProductId: string, optionKey: string, value: string) => {
    setFieldErrors((prev) => ({
      ...prev,
      [orderProductId]: (prev[orderProductId] || []).filter((key) => key !== optionKey),
    }));
    setSelectedOptions((prev) => ({
      ...prev,
      [orderProductId]: {
        ...(prev[orderProductId] || {}),
        [optionKey]: value,
      },
    }));
  };

  const orderProducts = useMemo(() => orderData?.order_products || [], [orderData]);
  const optionColumns = useMemo(() => {
    const optionMap = new Map<string, ProductOptionDefinition>();
    orderProducts.forEach((item: any) => {
      const rowOptions = optionsByProductId[String(item?.store_product_id)] || [];
      rowOptions.forEach((option) => {
        if (!optionMap.has(option.key)) {
          optionMap.set(option.key, option);
        }
      });
    });
    return Array.from(optionMap.values()).sort((a, b) => {
      if (a.optionType !== b.optionType) return a.optionType - b.optionType;
      return a.label.localeCompare(b.label);
    });
  }, [orderProducts, optionsByProductId]);

  const handleSubmit = async () => {
    if (!orderData || loading || isSubmitting) return;
    if (isOrderUpToDate) {
      toast.info("This order is already up to date.");
      return;
    }
    const token = searchParams.get("token");
    if (!token) {
      toast.error("Invalid link. Missing token.");
      return;
    }

    const validationErrors: Record<string, string[]> = {};
    const payloadProducts = orderProducts.map((item: any) => {
      const optionDefinitions =
        optionsByProductId[String(item?.store_product_id)] || [];
      const selectedForRow = selectedOptions[String(item.id)] || {};

      const optionsPayload = optionDefinitions.map((definition) => {
        const value = String(selectedForRow[definition.key] || "").trim();
        if (!value) {
          if (!validationErrors[String(item.id)]) {
            validationErrors[String(item.id)] = [];
          }
          validationErrors[String(item.id)].push(definition.key);
        }
        return {
          product_option_id: Number(definition.key),
          product_option_value: value,
        };
      });

      return {
        order_product_id: item.id,
        options: optionsPayload,
      };
    });

    if (Object.keys(validationErrors).length > 0) {
      setFieldErrors(validationErrors);
      toast.error("Please select all required options before submitting.");
      return;
    }

    try {
      setIsSubmitting(true);
      setFieldErrors({});
      await axios.post(
        `${process.env.REACT_APP_BASE_URL}/order/update-product-options/${DIRECT_ACCESS_URL}`,
        {
          order_number: String(orderData?.customer_order_id || ""),
          email: String(orderData?.email || ""),
          products: payloadProducts,
        },
        {
          headers: {
            "x-auth": token,
          },
        }
      );
      toast.success("Order product options updated successfully.");
      setIsSubmittedSuccessfully(true);
      setIsOrderUpToDate(true);
    } catch (error: any) {
      console.error("Update product options failed:", error?.response?.data || error);
      if (error?.response?.status === 409) {
        navigate("/page-not-found", { replace: true });
        return;
      }
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Unable to submit options. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };
 
  return (
    <div className={styles.renWrap}>
      <div className={styles.renHeader}>
        <img src="/images/logo.svg" alt="REN Athletics" className={styles.headerLogo} />
      </div>

      <div className={styles.renBody}>
        {isSubmittedSuccessfully ? (
          <div className={styles.successScreen}>
            <p className={styles.successOrderNumber}>
              Order Number #{orderData?.customer_order_id || orderData?.id || "-"}
            </p>
            <h2 className={styles.successTitle}>Thank You!</h2>
            <p className={styles.successText}>
              Your product options have been submitted successfully.
            </p>
          </div>
        ) : (
          <>
        <div className={styles.orderMeta}>
          <div>
            <p className={styles.orderNum}>
              Order Number #{orderData?.customer_order_id || orderData?.id || "-"}
            </p>
            <div className={styles.storeText}>
              Store: <strong>{orderData?.Store_detail?.store_name || "REN Athletics"}</strong>
            </div>
          </div>
          <div className={styles.orderDateBlock}>
            Order Date
            <br />
            <strong>
              {orderData?.createdAt
                ? new Date(orderData.createdAt).toLocaleDateString("en-US", {
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                  })
                : "-"}
            </strong>
          </div>
        </div>
      
        <div className={styles.contactBlock}>
          <p>
            <strong>Contact Information</strong>
          </p>
        
          <p>Email: {orderData?.Store_detail?.customer_email || "-"}</p>
          <p>Phone: {orderData?.Store_detail?.customer_phone_number
 || "-"}</p>
        </div>

        <hr className={styles.divider} />

        <div className={styles.noticeBox}>
          <p>
            Due to a recent maintenance issue, we were unable to capture the{" "}
            <strong>size and color</strong> details for your order. Please review
            the items below and select the correct options, then hit{" "}
            <strong>Submit</strong> - we'll get your order processed right away.
          </p>
        </div>

        {!isSubmittedSuccessfully ? (
          <p className={styles.sectionTitle}>Ordered Items ({orderProducts.length})</p>
        ) : null}

        {!isSubmittedSuccessfully ? (
        <div className={styles.tableWrap}>
          <table className={styles.prodTable}>
            <thead>
              <tr>
                <th style={{ width: "56px" }}>Product Images</th>
                <th>Product Details</th>
                <th className={styles.center} style={{ width: "70px" }}>
                  Quantity
                </th>
                {optionColumns.map((column) => (
                  <th
                    key={column.key}
                    className={styles.center}
                    style={{ width: "180px" }}
                  >
                    {column.label} <span className={styles.requiredMark}>*</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={3 + optionColumns.length} className={styles.stateCell}>
                    Loading order details...
                  </td>
                </tr>
              ) : errorMessage ? (
                <tr>
                  <td colSpan={3 + optionColumns.length} className={styles.errorCell}>
                    {errorMessage}
                  </td>
                </tr>
              ) : orderProducts.length === 0 ? (
                <tr>
                  <td colSpan={3 + optionColumns.length} className={styles.stateCell}>
                    No products found for this order.
                  </td>
                </tr>
              ) : (
                orderProducts.map((item: any) => {
                  const allOptions =
                    optionsByProductId[String(item?.store_product_id)] || [];

                  return (
                    <tr key={item.id}>
                  <td>
                    <div className={styles.prodImg}>
                      <img
                        src={
                          item?.store_product?.store_product_images?.[0]?.image
                            ? `${IMAGE_BASE_URL}${item?.store_product?.store_product_images?.[0]?.image}`
                            : "/images/default-product.png"
                        }
                        alt={item?.store_product?.product_name || "Product"}
                        className={styles.prodImageTag}
                      />
                    </div>
                  </td>
                  <td>
                    <p className={styles.prodName}>{item?.store_product?.product_name || "-"}</p>
                  </td>
                  <td className={styles.qtyCell}>{item?.quantity || 0}</td>
                  {optionColumns.map((column) => {
                    const option = allOptions.find((rowOption) => rowOption.key === column.key);
                    if (!option) {
                      return (
                        <td key={`${item.id}-${column.key}`}>
                          <span className={styles.noExtraOption}>-</span>
                        </td>
                      );
                    }
                    return (
                      <td key={`${item.id}-${column.key}`} className={styles.selCell}>
                        {option.inputType === 1 ? (
                          <select
                            value={selectedOptions?.[String(item.id)]?.[option.key] || ""}
                            onChange={(event) =>
                              handleOptionChange(String(item.id), option.key, event.target.value)
                            }
                          >
                            <option value="">-- Select --</option>
                            {option.values.map((value) => (
                              <option key={value} value={value}>
                                {value}
                              </option>
                            ))}
                          </select>
                        ) : option.inputType === 3 ? (
                          <textarea
                            value={selectedOptions?.[String(item.id)]?.[option.key] || ""}
                            onChange={(event) =>
                              handleOptionChange(String(item.id), option.key, event.target.value)
                            }
                            placeholder={`Enter ${option.label}`}
                            rows={3}
                          />
                        ) : option.inputType === 4 ? (
                          <select
                            value={selectedOptions?.[String(item.id)]?.[option.key] || ""}
                            onChange={(event) =>
                              handleOptionChange(String(item.id), option.key, event.target.value)
                            }
                          >
                            <option value="">-- Select --</option>
                            <option value="Yes">Yes</option>
                            <option value="No">No</option>
                          </select>
                        ) : (
                          <input
                            type="text"
                            value={selectedOptions?.[String(item.id)]?.[option.key] || ""}
                            onChange={(event) =>
                              handleOptionChange(String(item.id), option.key, event.target.value)
                            }
                            placeholder={`Enter ${option.label}`}
                          />
                        )}
                        {fieldErrors?.[String(item.id)]?.includes(option.key) ? (
                          <span className={styles.fieldError}>Required</span>
                        ) : null}
                      </td>
                    );
                  })}
                </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        ) : null}

        {!isSubmittedSuccessfully ? (
        <div className={styles.submitWrap}>
          <button
            className={styles.submitBtn}
            onClick={handleSubmit}
            disabled={
              isSubmitting ||
              loading ||
              Boolean(errorMessage) ||
              isOrderUpToDate ||
              isSubmittedSuccessfully
            }
          >
            {isSubmitting ? "Submitting..." : "Submit"}
          </button>
        </div>
        ) : null}
        </>
        )}

        <div className={styles.footerNote}>
          If you have any questions, please contact REN Athletics customer support.
          <br />
          Thank you for your order!
        </div>
      </div>
    </div>
  );
};

export default MissingProductOptions;
